import axios from 'axios';
import config from './config';

const API_URL = `${config.API_BASE_URL}/branches`;

// ==================== CORE CRUD ====================

export const fetchBranches = async () => {
  try {
    const response = await axios.get(API_URL);
    return response.data;
  } catch (error) {
    console.error('Error fetching branches:', error);
    throw error;
  }
};

export const fetchInactiveBranches = async () => {
  try {
    const response = await axios.get(`${API_URL}/inactive`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inactive branches:', error);
    throw error;
  }
};

export const fetchBranchById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching branch:', error);
    throw error;
  }
};

export const createBranch = async (branchData) => {
  try {
    const response = await axios.post(API_URL, branchData);
    return response.data;
  } catch (error) {
    console.error('Error creating branch:', error);
    throw error;
  }
};

export const updateBranch = async (id, branchData) => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, branchData);
    return response.data;
  } catch (error) {
    console.error('Error updating branch:', error);
    throw error;
  }
};

export const deleteBranch = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting branch:', error);
    throw error;
  }
};

// ==================== DELIVERY CITIES ====================

export const fetchBranchCities = async (branchId) => {
  try {
    const response = await axios.get(`${API_URL}/${branchId}/cities`);
    return response.data;
  } catch (error) {
    console.error('Error fetching branch cities:', error);
    throw error;
  }
};

export const assignBranchCities = async (branchId, cityIds) => {
  try {
    const response = await axios.put(`${API_URL}/${branchId}/cities`, { cityIds });
    return response.data;
  } catch (error) {
    console.error('Error assigning branch cities:', error);
    throw error;
  }
};

// ==================== 🆕 BRANCH CONNECTIONS ====================

export const fetchBranchConnections = async (branchId) => {
  try {
    const response = await axios.get(`${API_URL}/${branchId}/connections`);
    return response.data;
  } catch (error) {
    console.error('Error fetching branch connections:', error);
    throw error;
  }
};

export const assignBranchConnections = async (branchId, connectedBranchIds) => {
  try {
    const response = await axios.put(`${API_URL}/${branchId}/connections`, { connectedBranchIds });
    return response.data;
  } catch (error) {
    console.error('Error assigning branch connections:', error);
    throw error;
  }
};

export const removeBranchConnection = async (branchId, connectedBranchId) => {
  try {
    const response = await axios.delete(`${API_URL}/${branchId}/connections/${connectedBranchId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing branch connection:', error);
    throw error;
  }
};

// ==================== 🆕 RATINGS ====================

export const submitBranchRating = async (branchId, { userId, rating, comment }) => {
  try {
    const response = await axios.post(`${API_URL}/${branchId}/ratings`, { userId, rating, comment });
    return response.data;
  } catch (error) {
    console.error('Error submitting branch rating:', error);
    throw error;
  }
};

export const fetchBranchRatings = async (branchId, page = 1, limit = 20) => {
  try {
    const response = await axios.get(`${API_URL}/${branchId}/ratings`, {
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching branch ratings:', error);
    throw error;
  }
};

export const fetchUserRatingForBranch = async (branchId, userId) => {
  try {
    const response = await axios.get(`${API_URL}/${branchId}/ratings/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user rating:', error);
    throw error;
  }
};

export const deleteBranchRating = async (branchId, userId) => {
  try {
    const response = await axios.delete(`${API_URL}/${branchId}/ratings/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting branch rating:', error);
    throw error;
  }
};

// ==================== 🆕 LIKES / DISLIKES ====================

export const toggleBranchLike = async (branchId, { userId, type }) => {
  try {
    const response = await axios.post(`${API_URL}/${branchId}/likes`, { userId, type });
    return response.data;
  } catch (error) {
    console.error('Error toggling branch like:', error);
    throw error;
  }
};

export const fetchBranchLikes = async (branchId) => {
  try {
    const response = await axios.get(`${API_URL}/${branchId}/likes`);
    return response.data;
  } catch (error) {
    console.error('Error fetching branch likes:', error);
    throw error;
  }
};

export const fetchUserLikeForBranch = async (branchId, userId) => {
  try {
    const response = await axios.get(`${API_URL}/${branchId}/likes/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user like status:', error);
    throw error;
  }
};

// ==================== 🆕 DISPLAY ORDER ====================

export const updateBranchDisplayOrder = async (orders) => {
  // orders = [{ branchId: 1, displayOrder: 0 }, { branchId: 2, displayOrder: 1 }]
  try {
    const response = await axios.put(`${API_URL}/display-order/bulk`, { orders });
    return response.data;
  } catch (error) {
    console.error('Error updating display order:', error);
    throw error;
  }
};

// ==================== 🆕 FEATURED ====================

export const setBranchFeatured = async (branchId, isFeatured) => {
  try {
    const response = await axios.put(`${API_URL}/${branchId}/featured`, { isFeatured });
    return response.data;
  } catch (error) {
    console.error('Error setting branch featured:', error);
    throw error;
  }
};

export const fetchFeaturedBranches = async () => {
  try {
    const response = await axios.get(`${API_URL}/featured`);
    return response.data;
  } catch (error) {
    console.error('Error fetching featured branches:', error);
    throw error;
  }
};